import { Injectable, TemplateRef } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ModalTipo = 'info' | 'success' | 'warning' | 'error';

export interface ModalConfig {
    titulo?: string;
    mensaje?: string;
    tipo?: ModalTipo;
    textoBoton?: string;
    textoBotonCancelar?: string;
    mostrarBoton?: boolean;
    /** Milisegundos para cerrarse solo. 0 = sin autocierre. */
    tiempoAutoCierre?: number;
    /** Contenido proyectado (componentes: stepper, tablas, etc.). */
    contenido?: TemplateRef<any>;
    /** Ancho máximo de la card. Sin valor, queda el 400px por defecto. */
    ancho?: string;
    /** Variante visual del popup. 'solicitud' replica el modal de éxito de la app. */
    variante?: 'solicitud';
    /**
     * Aviso crítico: mientras esté visible, otros `cerrar()` / `abrirPopup`
     * no lo pueden limpiar ni reemplazar. Solo lo cierra su propio botón.
     */
    sticky?: boolean;
    accionConfirmar?: () => void;
    accionCerrar?: () => void;
}

export interface ModalState extends ModalConfig {
    show: boolean;
}

const ESTADO_INICIAL: ModalState = {
    show: false,
    titulo: '',
    mensaje: '',
    tipo: 'warning',
    textoBoton: 'Aceptar',
    textoBotonCancelar: '',
    mostrarBoton: true,
    tiempoAutoCierre: 0,
    contenido: undefined,
    ancho: undefined,
    variante: undefined,
    sticky: false,
};

/**
 * Estado central del popup custom.
 *
 * Reemplaza el `PopupState` que cada componente duplicaba (`popup` +
 * `abrirPopup/cerrarPopup/confirmarPopup`) por un único estado global que
 * consume el host `app-modal-custom-host`.
 *
 * Cada `abrirPopup` arranca desde los valores por defecto para que no se
 * filtre config de una apertura anterior (ej. el botón cancelar).
 */
@Injectable({ providedIn: 'root' })
export class PopUpModalCustomService {

    private readonly state$ = new BehaviorSubject<ModalState>({ ...ESTADO_INICIAL });

    readonly modal$: Observable<ModalState> = this.state$.asObservable();

    abrirPopup(config: ModalConfig): void {
        const actual = this.state$.value;
        // Un aviso sticky visible no se deja reemplazar por otros popups.
        if (actual.show && actual.sticky && !config.sticky) {
            return;
        }
        this.state$.next({ ...ESTADO_INICIAL, ...config, show: true });
    }

    confirmar(): void {
        // El botón del propio aviso siempre cierra (incluido el sticky).
        const accionConfirmar = this.state$.value.accionConfirmar;
        this.state$.next({ ...ESTADO_INICIAL });
        if (accionConfirmar) {
            accionConfirmar();
        }
    }

    cerrar(): void {
        const actual = this.state$.value;
        // Un aviso sticky solo se cierra con su propio botón (confirmar()).
        if (actual.show && actual.sticky) {
            return;
        }
        const accionCerrar = actual.accionCerrar;
        this.state$.next({ ...ESTADO_INICIAL });
        if (accionCerrar) {
            accionCerrar();
        }
    }
}
